import React, { useState, useRef } from 'react';
import { getDailyExpensesSummary, openPaymentDocumentFile, openExpenseDocumentFile } from '../api/client';
import { useStores } from '../contexts/StoreContext';
import html2canvas from 'html2canvas';

// ── helpers ──────────────────────────────────────────────────────────────────

const fmtUSD = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

const fmtVES = (v) =>
  new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

const fmtDateLong = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-VE', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  });
};

const AUDIT_LABELS = {
  pending: { text: 'Pendiente', color: '#b45309', bg: '#fef3c7' },
  conforme: { text: 'Conforme', color: '#15803d', bg: '#dcfce7' },
  observado: { text: 'Observado', color: '#b91c1c', bg: '#fee2e2' },
};

// ── sub-componentes de presentación ──────────────────────────────────────────

const SectionHeader = ({ title, right, color = '#1e40af' }) => (
  <div
    style={{
      backgroundColor: color,
      color: '#fff',
      padding: '10px 18px',
      borderRadius: '6px 6px 0 0',
      fontSize: '15px',
      fontWeight: 'bold',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <span>{title}</span>
    {right && <span style={{ fontSize: '13px', fontWeight: 600 }}>{right}</span>}
  </div>
);

const SectionBody = ({ children, noPadding = false }) => (
  <div
    style={{
      border: '1px solid #d1d5db',
      borderTop: 'none',
      borderRadius: '0 0 6px 6px',
      padding: noPadding ? 0 : '18px',
      backgroundColor: '#fff',
      marginBottom: '26px',
    }}
  >
    {children}
  </div>
);

const MetricCard = ({ label, value, sub, color = '#2563eb', bg = '#eff6ff' }) => (
  <div
    style={{
      backgroundColor: bg,
      border: `2px solid ${color}`,
      borderRadius: '8px',
      padding: '14px 16px',
      flex: 1,
      minWidth: '150px',
    }}
  >
    <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </div>
    <div style={{ fontSize: '20px', fontWeight: 'bold', color, marginTop: '4px' }}>{value}</div>
    {sub && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '3px' }}>{sub}</div>}
  </div>
);

const Th = ({ children, align = 'left', width }) => (
  <th
    style={{
      backgroundColor: '#f3f4f6',
      padding: '8px 10px',
      textAlign: align,
      fontSize: '11px',
      fontWeight: '600',
      color: '#374151',
      borderBottom: '2px solid #d1d5db',
      whiteSpace: 'nowrap',
      width,
    }}
  >
    {children}
  </th>
);

const Td = ({ children, align = 'left', bold = false, color = '#111827', nowrap = false }) => (
  <td
    style={{
      padding: '6px 10px',
      fontSize: '12px',
      textAlign: align,
      fontWeight: bold ? '700' : '400',
      color,
      borderBottom: '1px solid #f3f4f6',
      whiteSpace: nowrap ? 'nowrap' : 'normal',
      verticalAlign: 'top',
    }}
  >
    {children}
  </td>
);

const Badge = ({ text, color, bg }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 7px',
      borderRadius: '10px',
      fontSize: '10px',
      fontWeight: 600,
      color,
      backgroundColor: bg,
      whiteSpace: 'nowrap',
    }}
  >
    {text}
  </span>
);

// ── componente principal ─────────────────────────────────────────────────────

const DailyExpenseReport = () => {
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const [selectedDate, setSelectedDate] = useState(todayIso);
  const [selectedStore, setSelectedStore] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const reportRef = useRef(null);
  const { stores } = useStores();

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const result = await getDailyExpensesSummary(selectedDate, selectedStore || null);
      setData(result);
    } catch (e) {
      setError('Error al cargar los datos: ' + (e.response?.data?.detail || e.message || 'error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!reportRef.current) return;
    setGenerating(true);
    try {
      if (!window.jspdf) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        });
      }
      await new Promise((res) => setTimeout(res, 200));

      const canvas = await html2canvas(reportRef.current, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 0,
      });

      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const imgH = (canvas.height / canvas.width) * pageW;

      let offset = 0;
      while (offset < imgH) {
        if (offset > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, -offset, pageW, imgH);
        offset += pageH;
      }
      const storeSlug = data?.store_name ? `-${data.store_name.replace(/[^A-Za-z0-9]+/g, '_')}` : '';
      pdf.save(`Resumen-Diario-Gastos-Farmavida${storeSlug}-${selectedDate}.pdf`);
    } catch (e) {
      console.error('PDF error:', e);
      alert('Error generando PDF: ' + e.message);
    } finally {
      setGenerating(false);
    }
  };

  const [openingDoc, setOpeningDoc] = useState(null);

  const handleOpenDoc = async (doc) => {
    setOpeningDoc(doc.file_id);
    try {
      if (doc.payment_id) await openPaymentDocumentFile(doc.payment_id, doc.file_id);
      else await openExpenseDocumentFile(doc.expense_id, doc.file_id);
    } catch (e) {
      alert('No se pudo abrir el comprobante: ' + (e.response?.data?.detail || e.message || 'error'));
    } finally {
      setOpeningDoc(null);
    }
  };

  // La columna de comprobantes es solo para pantalla: se oculta al generar el PDF
  const showDocs = !generating;

  const DocsCell = ({ documents }) => {
    const docs = documents || [];
    if (docs.length === 0) {
      return <Td align="center" color="#9ca3af">—</Td>;
    }
    return (
      <Td align="center" nowrap>
        {docs.map((doc, idx) => (
          <button
            key={doc.file_id}
            onClick={() => handleOpenDoc(doc)}
            disabled={openingDoc === doc.file_id}
            title={doc.filename}
            style={{
              cursor: 'pointer',
              border: '1px solid #bfdbfe',
              backgroundColor: openingDoc === doc.file_id ? '#e5e7eb' : '#eff6ff',
              color: '#1d4ed8',
              borderRadius: '5px',
              padding: '3px 7px',
              fontSize: '11px',
              fontWeight: 600,
              marginLeft: idx > 0 ? '4px' : 0,
            }}
          >
            {openingDoc === doc.file_id ? '...' : `📎 Ver${docs.length > 1 ? ` ${idx + 1}` : ''}`}
          </button>
        ))}
      </Td>
    );
  };

  const t = data?.totals || {};
  const hasData = data && (data.supplier_payments?.length > 0 || data.categories?.length > 0);

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Controles */}
      <div className="bg-white rounded-lg shadow p-5 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Resumen Diario de Gastos</h1>
        <p className="text-sm text-gray-500 mb-4">
          Todos los gastos del día: pagos a proveedores agrupados por proveedor y el resto por categoría.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fecha</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tienda</label>
            <select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[200px]"
            >
              <option value="">Todas las tiendas</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            {loading ? 'Cargando...' : 'Generar resumen'}
          </button>
          {data && (
            <button
              onClick={handleExportPDF}
              disabled={generating}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              {generating ? 'Generando PDF...' : '⬇ Descargar PDF'}
            </button>
          )}
        </div>
        {error && <div className="mt-3 text-sm text-red-600">{error}</div>}
      </div>

      {/* Reporte */}
      {data && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <div
            ref={reportRef}
            style={{ width: '800px', padding: '32px', backgroundColor: '#fff', fontFamily: 'Arial, Helvetica, sans-serif', margin: '0 auto' }}
          >
            {/* Encabezado */}
            <div style={{ borderBottom: '3px solid #1e40af', paddingBottom: '14px', marginBottom: '24px' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e40af' }}>FARMAVIDA</div>
              <div style={{ fontSize: '17px', fontWeight: 600, color: '#111827', marginTop: '2px' }}>
                Resumen Diario de Gastos
              </div>
              <div style={{ fontSize: '13px', color: '#374151', marginTop: '6px', textTransform: 'capitalize' }}>
                {fmtDateLong(data.date)}
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                {data.store_name ? `Tienda: ${data.store_name}` : 'Todas las tiendas'}
                {data.exchange_rate ? ` · Tasa: ${fmtVES(data.exchange_rate)} Bs/USD` : ''}
                {' · '}Generado por {data.generated_by} el {new Date(data.generated_at).toLocaleString('es-VE')}
              </div>
            </div>

            {/* Resumen */}
            <SectionHeader title="RESUMEN DEL DÍA" />
            <SectionBody>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <MetricCard
                  label="Total gastado"
                  value={fmtUSD(t.total_usd)}
                  sub={`Bs ${fmtVES(t.total_ves)} · ${t.expense_count || 0} gastos`}
                  color="#dc2626"
                  bg="#fef2f2"
                />
                <MetricCard
                  label="Pagos a proveedores"
                  value={fmtUSD(t.supplier_expenses_usd)}
                  sub={`Bs ${fmtVES(t.supplier_expenses_ves)}`}
                  color="#7c3aed"
                  bg="#f5f3ff"
                />
                <MetricCard
                  label="Otros gastos"
                  value={fmtUSD(t.other_expenses_usd)}
                  sub={`Bs ${fmtVES(t.other_expenses_ves)}`}
                  color="#d97706"
                  bg="#fffbeb"
                />
              </div>

              <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
                Desglose por método de pago
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <Th>Método</Th>
                    <Th align="center">Gastos</Th>
                    <Th align="right">Monto USD</Th>
                    <Th align="right">Monto Bs</Th>
                  </tr>
                </thead>
                <tbody>
                  {(data.payment_methods || []).map((m) => (
                    <tr key={m.method}>
                      <Td>{m.label}</Td>
                      <Td align="center">{m.count}</Td>
                      <Td align="right" bold>{fmtUSD(m.total_usd)}</Td>
                      <Td align="right">{fmtVES(m.total_ves)}</Td>
                    </tr>
                  ))}
                  {(data.payment_methods || []).length === 0 && (
                    <tr><Td align="center" color="#6b7280">Sin gastos registrados</Td></tr>
                  )}
                </tbody>
              </table>
            </SectionBody>

            {/* Pagos a proveedores */}
            <SectionHeader
              title="PAGOS A PROVEEDORES"
              right={`${fmtUSD(t.supplier_payments_usd)} · Bs ${fmtVES(t.supplier_payments_ves)}`}
              color="#7c3aed"
            />
            <SectionBody noPadding>
              {(t.payments_without_document > 0 || t.payments_incomplete_document > 0) && (
                <div style={{ padding: '9px 14px', fontSize: '11px', color: '#92400e', backgroundColor: '#fffbeb', borderBottom: '1px solid #fde68a', fontWeight: 600 }}>
                  ⚠️ Revisar respaldos:
                  {t.payments_without_document > 0 && ` ${t.payments_without_document} pago(s) sin comprobante.`}
                  {t.payments_incomplete_document > 0 && ` ${t.payments_incomplete_document} pago(s) con menos comprobantes que referencias bancarias.`}
                </div>
              )}
              {(data.supplier_payments || []).length === 0 ? (
                <div style={{ padding: '16px', fontSize: '12px', color: '#6b7280' }}>
                  No se registraron pagos a proveedores este día.
                </div>
              ) : (
                (data.supplier_payments || []).map((g) => (
                  <div key={g.supplier} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: '#faf5ff',
                        padding: '8px 14px',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: '#5b21b6',
                      }}
                    >
                      <span>{g.supplier} <span style={{ fontWeight: 400, color: '#6b7280' }}>({g.invoice_count} factura{g.invoice_count === 1 ? '' : 's'})</span></span>
                      <span>{fmtUSD(g.total_usd)} · Bs {fmtVES(g.total_ves)}</span>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <Th>Factura</Th>
                          <Th>Tienda</Th>
                          <Th align="right">Pagado USD</Th>
                          <Th align="right">Pagado Bs</Th>
                          <Th align="right">Saldo</Th>
                          <Th>Método / Referencia</Th>
                          <Th>Registró</Th>
                          {showDocs && <Th align="center">Comprob.</Th>}
                        </tr>
                      </thead>
                      <tbody>
                        {g.invoices.map((inv) => {
                          const audit = AUDIT_LABELS[inv.audit_status] || AUDIT_LABELS.pending;
                          return (
                            <tr key={inv.payment_id}>
                              <Td bold nowrap>
                                #{inv.invoice_number}
                                {!inv.has_document && <span title="Sin comprobante"> ⚠️</span>}
                                {inv.has_document && inv.documents_incomplete && (
                                  <span title="Faltan comprobantes"> ⚠️</span>
                                )}
                              </Td>
                              <Td>{inv.store_name}</Td>
                              <Td align="right" bold>{fmtUSD(inv.amount_usd)}</Td>
                              <Td align="right">{fmtVES(inv.amount_ves)}</Td>
                              <Td align="right" color={inv.fully_paid ? '#15803d' : '#b45309'}>
                                {inv.fully_paid ? 'Pagada' : fmtUSD(inv.remaining_usd)}
                              </Td>
                              <Td>
                                <div>{inv.payment_method_label}</div>
                                {inv.references?.length > 0 && (
                                  <div style={{ fontSize: '10px', color: '#6b7280' }}>Ref: {inv.references.join(', ')}</div>
                                )}
                                {inv.documents_incomplete && (
                                  <div style={{ fontSize: '10px', color: '#b45309', fontWeight: 600, marginTop: '2px' }}>
                                    ⚠️ {inv.document_count} comprobante{inv.document_count === 1 ? '' : 's'} para {inv.reference_count} referencias
                                  </div>
                                )}
                                <div style={{ marginTop: '3px' }}>
                                  <Badge text={audit.text} color={audit.color} bg={audit.bg} />
                                </div>
                              </Td>
                              <Td>{inv.user_name}</Td>
                              {showDocs && <DocsCell documents={inv.documents} />}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))
              )}
              {Math.abs(t.supplier_mismatch_usd || 0) > 0.01 && (
                <div style={{ padding: '10px 14px', fontSize: '11px', color: '#b45309', backgroundColor: '#fffbeb' }}>
                  ⚠️ Hay {fmtUSD(Math.abs(t.supplier_mismatch_usd))} registrados como pago a proveedor sin factura
                  vinculada {t.supplier_mismatch_usd > 0 ? '(gasto sin pago de factura asociado)' : '(pago de factura sin gasto asociado)'}.
                </div>
              )}
            </SectionBody>

            {/* Resto de categorías */}
            <SectionHeader
              title="OTROS GASTOS POR CATEGORÍA"
              right={`${fmtUSD(t.other_expenses_usd)} · Bs ${fmtVES(t.other_expenses_ves)}`}
              color="#d97706"
            />
            <SectionBody noPadding>
              {(data.categories || []).length === 0 ? (
                <div style={{ padding: '16px', fontSize: '12px', color: '#6b7280' }}>
                  No se registraron otros gastos este día.
                </div>
              ) : (
                (data.categories || []).map((g) => (
                  <div key={g.category} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: '#fffbeb',
                        padding: '8px 14px',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: '#92400e',
                      }}
                    >
                      <span>{g.label} <span style={{ fontWeight: 400, color: '#6b7280' }}>({g.item_count} gasto{g.item_count === 1 ? '' : 's'})</span></span>
                      <span>{fmtUSD(g.total_usd)} · Bs {fmtVES(g.total_ves)}</span>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <Th>Concepto</Th>
                          <Th>Tienda</Th>
                          <Th align="right">USD</Th>
                          <Th align="right">Bs</Th>
                          <Th>Método / Referencia</Th>
                          <Th>Registró</Th>
                          {showDocs && <Th align="center">Comprob.</Th>}
                        </tr>
                      </thead>
                      <tbody>
                        {g.items.map((it) => (
                          <tr key={it.id}>
                            <Td>
                              <span style={{ fontWeight: 600 }}>{it.concept}</span>
                              {!it.has_document && <span title="Sin comprobante"> ⚠️</span>}
                              {it.description && (
                                <div style={{ fontSize: '10px', color: '#6b7280' }}>{it.description}</div>
                              )}
                            </Td>
                            <Td>{it.store_name}</Td>
                            <Td align="right" bold>{fmtUSD(it.amount_usd)}</Td>
                            <Td align="right">{fmtVES(it.amount_ves)}</Td>
                            <Td>
                              <div>{it.payment_method_label}</div>
                              {it.references?.length > 0 && (
                                <div style={{ fontSize: '10px', color: '#6b7280' }}>Ref: {it.references.join(', ')}</div>
                              )}
                            </Td>
                            <Td>{it.user_name}</Td>
                            {showDocs && <DocsCell documents={it.documents} />}
                          </tr>
                        ))}
                        {(g.auto_items || []).map((a) => (
                          <tr key={`auto-${a.concept}-${a.description || ''}`} style={{ backgroundColor: '#f9fafb' }}>
                            <Td>
                              <span style={{ fontWeight: 600 }}>{a.concept}</span>
                              <span style={{ fontSize: '10px', color: '#6b7280' }}> · {a.count} movimiento{a.count === 1 ? '' : 's'}</span>
                              {a.description && (
                                <div style={{ fontSize: '10px', color: '#6b7280' }}>{a.description}</div>
                              )}
                            </Td>
                            <Td>{a.store_name}</Td>
                            <Td align="right" bold>{fmtUSD(a.amount_usd)}</Td>
                            <Td align="right">{fmtVES(a.amount_ves)}</Td>
                            <Td color="#6b7280">Cargo bancario</Td>
                            <Td color="#6b7280">Automático</Td>
                            {showDocs && <Td align="center" color="#9ca3af">—</Td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))
              )}
            </SectionBody>

            {/* Total final */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#1e40af',
                color: '#fff',
                padding: '14px 18px',
                borderRadius: '6px',
                fontSize: '16px',
                fontWeight: 'bold',
              }}
            >
              <span>TOTAL GASTADO EL DÍA</span>
              <span>{fmtUSD(t.total_usd)} · Bs {fmtVES(t.total_ves)}</span>
            </div>

            <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '14px', textAlign: 'center' }}>
              ⚠️ indica gastos o pagos sin comprobante adjunto en el sistema. Los cargos bancarios
              automáticos (comisiones, mantenimiento) se muestran agrupados por concepto. ·
              Farmavida — Sistema de Gestión
            </div>
          </div>
        </div>
      )}

      {data && !hasData && (
        <div className="text-center text-sm text-gray-500 mt-4">
          No hay gastos registrados para esta fecha.
        </div>
      )}
    </div>
  );
};

export default DailyExpenseReport;
